/**
 * Created by shanie on 1/18/17.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';

// Components
import DraggableTiles from '../Tiles/DraggableTiles';

// Helpers
import actions from '../../actions/FavoritesActions';
import classnames from 'classnames';
import get from 'lodash.get';
import { cutoffs, slugify, tabs } from '../../constants';

// Styles
import styles from '../Tiles/styles/Tile.scss';

const SubPreviews = ({ imageClasses, src }) => {
    return (
        <div className={imageClasses}>{src && <img className={styles.preview} src={src} />}</div>
    );
};

SubPreviews.propTypes = {
    imageClasses: PropTypes.string,
    src: PropTypes.string,
};

const FolderPreviews = ({ portfolio, folderClasses, index, linkFolder }) => {
    const folderUrl = `/favorites${slugify(portfolio.name, portfolio.serviceId)}`;
    const subImageEdges = portfolio.items.edges.concat([{}, {}, {}, {}]).slice(1, 4);
    const mainImage = get(portfolio, 'items.edges[0].node.item.photos[0].versions[0].webPath', '');
    const subImages = subImageEdges.map((edge, i) => {
        const src = get(edge, 'node.item.photos[0].versions[0].webPath', '');
        const imageClasses = classnames('colXs4', styles.subImage, {
            [styles.middle]: i === 1,
            [styles.end]: i !== 1,
        });
        return (
            <SubPreviews
                key={`${i} ${portfolio.serviceId}`}
                portfolioId={portfolio.serviceId}
                src={src}
                imageClasses={imageClasses}
            />
        );
    });

    return (
        <div
            className={`colXs12 colMd6 colLg4 colXl3 ${styles.container}`}
            data-tn={`folder-outer-container-${index}`}
        >
            <div className={`${styles.spacers} ${folderClasses}`}>
                <a
                    href={folderUrl}
                    className={styles.folder}
                    data-tn={`folder-container-${index}`}
                    onClick={event => linkFolder(event, portfolio.serviceId, portfolio.name)}
                >
                    <div className={styles.main}>
                        {mainImage && <img className={styles.preview} src={mainImage} />}
                    </div>
                    <div className={styles.sub}>{subImages}</div>
                    <div className={styles.title} data-tn="folder-title">
                        {portfolio.name} ({portfolio.items.totalResults})
                    </div>
                </a>
            </div>
        </div>
    );
};

FolderPreviews.propTypes = {
    index: PropTypes.number,
    linkFolder: PropTypes.func,
    portfolio: PropTypes.object,
    folderClasses: PropTypes.string,
    selectedItems: PropTypes.array,
};

class PortfolioFolders extends React.Component {
    constructor() {
        super();

        this.state = {
            items: null,
            dragIndex: null,
            overIndex: null,
        };

        this.dropHandler = this.dropHandler.bind(this);
        this.linkFolder = this.linkFolder.bind(this);
        this.getFolders = this.getFolders.bind(this);
        this.updateSelectAllStatus = this.updateSelectAllStatus.bind(this);
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.portfoliosConnection) {
            const portfolios = this.getFolders(nextProps.portfoliosConnection);
            this.updateSelectAllStatus(
                portfolios,
                nextProps.selectItems,
                nextProps.selectAllStatus
            );
        }
    }

    getFolders(portfoliosConnection) {
        return portfoliosConnection.edges.map(edge => {
            return { portfolioId: edge.node.serviceId, itemId: edge.node.serviceId };
        });
    }

    updateSelectAllStatus(portfolios, selectItems, selectAllStatus) {
        if (selectAllStatus === 'select' && this.props.selectAllStatus !== 'select') {
            selectItems(portfolios);
        } else if (selectAllStatus === 'deselect' && this.props.selectAllStatus !== 'deselect') {
            selectItems([]);
        }
    }

    linkFolder(event, portfolioId, portfolioName) {
        event.preventDefault();
        if (!this.props.selecting) {
            this.props.setNumItems(cutoffs.ADDITIONAL_ITEMS);
            this.props.setPortfolioIds([portfolioId]);
            this.props.setPortfolioName(portfolioName);
            this.props.setTab(tabs.FOLDER);
            this.props.setTabChanged(true);
            this.props.resetUiState();
        }
    }

    dropHandler(draggedFromIndex, draggedToIndex) {
        // Get set of portfolios prior to dragging
        const portfolios = this.props.portfoliosConnection.edges;
        // Get dragged portfolio's portfolioId
        const targetPortId = get(portfolios[draggedFromIndex], 'node.serviceId');
        let startPortId;
        let endPortId;
        // Find the adjacent portfolios and reorder the portfolios
        if (draggedFromIndex < draggedToIndex) {
            // If portfolio was moved to 0th position, there is no portfolio to it's left (no startPortId), otherwise startPortId
            // is the portfolioId of the portfolio at one index less than the index the portfolio was dragged to (draggedToIndex)
            startPortId =
                draggedToIndex <= 0 ? null : get(portfolios[draggedToIndex], 'node.serviceId');
            // If portfolios was moved to last position, there is no portfolio to it's right (no endPortId), otherwise endPortId
            // is the portfolioId of the portfolio at one index greater than the index the portfolio was dragged to (draggedToIndex)
            endPortId =
                draggedToIndex >= portfolios.length - 1
                    ? null
                    : get(portfolios[draggedToIndex + 1], 'node.serviceId');
        } else {
            // If portfolio was moved to 0th position, there is no portfolio to it's left (no startPortId), otherwise startPortId
            // is the portfolioId of the portfolio at one index less than the index the portfolio was dragged to (draggedToIndex)
            startPortId =
                draggedToIndex <= 0 ? null : get(portfolios[draggedToIndex - 1], 'node.serviceId');
            // If portfolios was moved to last position, there is no portfolio to it's right (no endPortId), otherwise endPortId
            // is the portfolioId of the portfolio at one index greater than the index the portfolio was dragged to (draggedToIndex)
            endPortId =
                draggedToIndex >= portfolios.length - 1
                    ? null
                    : get(portfolios[draggedToIndex], 'node.serviceId');
        }
        // Reorder the portfolios via a relay mutation
        this.props.setReordered(true);
        this.props.onDrop({
            action: 'REORDER',
            targetPortfolioId: targetPortId,
            startPortfolioId: startPortId,
            endPortfolioId: endPortId,
        });
    }

    render() {
        const { reordering, reordered, selectedItems, selecting } = this.props;
        const portfoliosEdges = get(this.props, 'portfoliosConnection.edges') || [];
        const portfolios = portfoliosEdges.map(edge => edge.node);
        const folderClasses = classnames(styles.folder, {
            [styles.boxShadows]: reordering || selecting,
            [styles.boxShadow]: !reordering && !selecting,
        });

        if (reordering) {
            const draggableItems = portfolios.map((portfolio, index) => {
                const src = get(
                    portfolio,
                    'items.edges[0].node.item.photos[0].versions[0].webPath',
                    ''
                );
                return { src, title: portfolio.name, marker: index, sort: index };
            });

            return (
                <div className={styles.dragContainer}>
                    <DraggableTiles
                        onDrop={this.dropHandler}
                        draggableItems={draggableItems}
                        reordered={reordered}
                    />
                </div>
            );
        }

        const portfoliosElements = portfolios.map((portfolio, index) => {
            return (
                <FolderPreviews
                    key={portfolio.serviceId}
                    index={index}
                    portfolio={portfolio}
                    folderClasses={folderClasses}
                    linkFolder={this.linkFolder}
                    selectedItems={selectedItems}
                />
            );
        });

        return <div className="rowFlex">{portfoliosElements}</div>;
    }
}

PortfolioFolders.propTypes = {
    items: PropTypes.array,
    onDrop: PropTypes.func,
    portfoliosConnection: PropTypes.object,
    resetUiState: PropTypes.func,
    reordered: PropTypes.bool,
    reordering: PropTypes.bool,
    selectAllStatus: PropTypes.string,
    selectedItems: PropTypes.array,
    selecting: PropTypes.bool,
    selectItems: PropTypes.func,
    setNumItems: PropTypes.func,
    setPortfolioIds: PropTypes.func,
    setPortfolioName: PropTypes.func,
    setPortfolioTypes: PropTypes.func,
    setReordered: PropTypes.func,
    setTabChanged: PropTypes.func,
    setTab: PropTypes.func,
    user: PropTypes.object,
};

const mapStateToProps = state => {
    return {
        reordering: state.favorites.uiState.reordering,
        selectAllStatus: state.favorites.uiState.selectAllStatus,
        selecting: state.favorites.uiState.selecting,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        resetUiState: () => {
            dispatch(actions.resetUiState());
        },
        selectItems: selectedItems => {
            dispatch(actions.selectItems({ selectedItems }));
        },
        setNumItems: first => {
            dispatch(actions.setNumItems({ first }));
        },
        setPortfolioIds: portfolioIds => {
            dispatch(actions.setPortfolioIds({ portfolioIds }));
        },
        setPortfolioName: portfolioName => {
            dispatch(actions.setPortfolioName({ portfolioName }));
        },
        setPortfolioTypes: portfolioTypes => {
            dispatch(actions.setPortfolioTypes({ portfolioTypes }));
        },
        setReordered: reordered => {
            dispatch(actions.setReordered({ reordered }));
        },
        setTab: tab => {
            dispatch(actions.setTab({ tab }));
        },
        setTabChanged: tabChanged => {
            dispatch(actions.setTabChanged({ tabChanged }));
        },
    };
};

PortfolioFolders = createFragmentContainer(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(PortfolioFolders),
    {
        portfoliosConnection: graphql`
            fragment PortfolioFolders_portfoliosConnection on PortfoliosConnection {
                edges {
                    node {
                        id
                        serviceId
                        name
                        items(first: 4, sortBy: CUSTOM) {
                            totalResults
                            numberOfPages
                            pageInfo {
                                startCursor
                                endCursor
                            }
                            edges {
                                node {
                                    item {
                                        photos {
                                            versions {
                                                webPath
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `,
    }
);

export default PortfolioFolders;
